import React, {useState} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import {useForm} from 'react-hook-form'
import {toast} from 'react-toastify'
import * as yup from 'yup'
import validator from 'validator'
import {PhoneNumberUtil} from 'google-libphonenumber'
import {yupResolver} from '@hookform/resolvers/yup'
import {useAuth0} from '@auth0/auth0-react'
import {ParagraphText, Modal, ModalTitle} from '@thryvlabs/maverick'
import {useAuthClient} from '../utils/use-auth-client'
import {useUserRoles} from '../utils/use-user-roles'
import {useAsync} from '../utils/use-async'
import {NewOrderLayout} from '../components/new-order-layout'
import {LoadingTimelineModal} from '../components/loading-timeline-modal'
import {Spinner} from '../components/loader-spinner'
import {AccountInformationForm} from '../components/account-information-components/account-information-form'
import {InvoiceInformation} from '../components/account-information-components/invoice-information'
import {fetchSelectedCountryRegionList} from '../utils/address-validation-util'
import {AccountFailedModalComponent} from '../components/account-failed-modal'

const phoneUtil = PhoneNumberUtil.getInstance()

const DIFFERENT_SALES_REP_OPTIONS = [
  {name: 'Sales Rep', value: true},
  {name: 'Partner', value: false},
  {name: 'No', value: 'NO'},
]
const OVERRIDE_SST_OPTIONS = [
  {name: 'Yes', value: true},
  {name: 'No', value: false},
]
const REQUIRED_FIELD_MESSAGE = 'This field is required'
const REQUIRED_OPTION_MESSAGE = 'Please select an option'
const MIN_CHARACTERS_REQUIRED = 'Must be at least 3 characters'
const MAX_CHARACTERS_REQUIRED = 'Must be at most 120 characters'
const SALES_REP_INFO_NOT_FOUND = 'Sales rep not Found.'
const VALID_EMAIL_MESSAGE = 'Must be a valid email'

const formatPostalCode = (postCode, country) => {
  if (country === 'CA' && postCode) {
    return postCode.slice(0, 3) + ' ' + postCode.slice(3)
  }
  return postCode
}

const SCHEMA = yup.object().shape({
  leadIDRequired: yup.boolean(),
  xpCodeReuired: yup.boolean(),
  firstName: yup
    .string()
    .required(REQUIRED_FIELD_MESSAGE)
    .min(3, MIN_CHARACTERS_REQUIRED)
    .max(120, MAX_CHARACTERS_REQUIRED),
  lastName: yup
    .string()
    .required(REQUIRED_FIELD_MESSAGE)
    .min(3, MIN_CHARACTERS_REQUIRED)
    .max(120, MAX_CHARACTERS_REQUIRED),
  xpCode: yup.string().when('xpCodeRequired', xpCodeRequired => {
    if (xpCodeRequired === true) {
      return yup
        .string()
        .required(REQUIRED_FIELD_MESSAGE)
        .min(3, MIN_CHARACTERS_REQUIRED)
        .max(120, MAX_CHARACTERS_REQUIRED)
    }
    return yup.string()
  }),
  keapAccount: yup.boolean(),
  email: yup
    .string()
    .required(REQUIRED_FIELD_MESSAGE)
    .email(VALID_EMAIL_MESSAGE),
  company: yup
    .string()
    .required(REQUIRED_FIELD_MESSAGE)
    .min(3, MIN_CHARACTERS_REQUIRED)
    .max(120, MAX_CHARACTERS_REQUIRED),
  leadID: yup.string().when('leadIDRequired', leadIDRequired => {
    if (leadIDRequired === true) {
      return yup
        .string()
        .required(REQUIRED_FIELD_MESSAGE)
        .min(3, MIN_CHARACTERS_REQUIRED)
        .max(120, MAX_CHARACTERS_REQUIRED)
    }
    return yup.string()
  }),
  keapID: yup.string().when('keapAccount', keapAccount => {
    if (keapAccount === true) {
      return yup.string().required()
    }
    return yup.string()
  }),
  postCode: yup
    .string()
    .required(REQUIRED_FIELD_MESSAGE)
    .test(
      'validate-postCode-format',
      'Incorrect post code format',
      function (value) {
        if (!value) {
          return false
        }
        const country = this.options.context.componentProps.selectedCountry
        if (country === 'BB') {
          return value.match(/^[A-Za-z]{2}\d{5}$/)
        }
        if (country === 'KY') {
          return value.match(/^[A-Za-z]{2}\d-?\d{4}$/)
        }
        if (country === 'CA') {
          return value.match(/^[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d$/)
        }
        return validator.isPostalCode(value, country)
      },
    ),
  address: yup
    .string()
    .required(REQUIRED_FIELD_MESSAGE)
    .min(3, MIN_CHARACTERS_REQUIRED)
    .max(120, MAX_CHARACTERS_REQUIRED),
  city: yup
    .string()
    .required(REQUIRED_FIELD_MESSAGE)
    .min(3, MIN_CHARACTERS_REQUIRED)
    .max(120, MAX_CHARACTERS_REQUIRED),
  state: yup.object().shape({
    name: yup.string().required(REQUIRED_OPTION_MESSAGE),
    value: yup.string().required(REQUIRED_OPTION_MESSAGE),
  }),
  mobile: yup
    .string()
    .required(REQUIRED_FIELD_MESSAGE)
    .test(
      'validate-phone-format',
      'Incorrect phone number format',
      function (value) {
        try {
          if (!value) {
            return false
          }
          const country = this.options.context.componentProps.selectedCountry
          value = value.replace(/[^\d]/g, '')
          if (country === 'US' && value.length > 10) {
            return false
          }
          const parsedPhone = phoneUtil.parse(value, country)
          return phoneUtil.isValidNumber(parsedPhone)
        } catch {
          return false
        }
      },
    ),
  companyPhone: yup
    .string()
    .required(REQUIRED_FIELD_MESSAGE)
    .test(
      'validate-phone-format',
      'Incorrect phone number format',
      function (value) {
        try {
          if (!value) {
            return false
          }
          const country = this.options.context.componentProps.selectedCountry
          value = value.replace(/[^\d]/g, '')
          if (country === 'US' && value.length > 10) {
            return false
          }
          const parsedPhone = phoneUtil.parse(value, country)
          return phoneUtil.isValidNumber(parsedPhone)
        } catch {
          return false
        }
      },
    ),
  differentSalesRep: yup
    .object()
    .shape({
      name: yup.string().required(REQUIRED_OPTION_MESSAGE),
      value: yup.string().required(REQUIRED_OPTION_MESSAGE),
    })
    .optional(),
  overrideRep: yup
    .object()
    .shape({
      name: yup.string().required(REQUIRED_OPTION_MESSAGE),
      value: yup.string().required(REQUIRED_OPTION_MESSAGE),
    })
    .optional(),

  salesRepCode: yup.string().when('differentSalesRep', {
    is: differentSalesRep => differentSalesRep.value === 'true',
    then: yup.string().required(REQUIRED_FIELD_MESSAGE),
  }),
  overrideRepCode: yup.string().when('overrideRep', {
    is: overrideRep => overrideRep.value === 'true',
    then: yup.string().required(REQUIRED_FIELD_MESSAGE),
  }),
  abn: yup.string().test('validate-abn', function (value) {
    const lowerCountry =
      this.options.context.componentProps.selectedCountry.toLowerCase()
    if (
      lowerCountry === 'au' &&
      (!value || value.length !== 11 || !/^\d+$/.test(value))
    ) {
      return this.createError({message: 'Please enter a valid, 11-digit ABN'})
    }
    if (
      lowerCountry === 'nz' &&
      value &&
      (value.length !== 13 || !/^\d+$/.test(value))
    ) {
      return this.createError({
        message: 'Please enter a valid, 13-digit NZBN',
      })
    }
    return true
  }),
  salesRepInfo: yup.string().when('differentSalesRep', {
    is: differentSalesRep => differentSalesRep.value === 'true',
    then: yup.string().required(REQUIRED_FIELD_MESSAGE),
  }),
  salesforceEmail: yup.string().email(VALID_EMAIL_MESSAGE),
  overrideRepInfo: yup.string().when('overrideRep', {
    is: overrideRep => overrideRep.value === 'true',
    then: yup.string().required(REQUIRED_FIELD_MESSAGE),
  }),
  sstSalesforceEmail: yup.string().email(VALID_EMAIL_MESSAGE),
})

const AccountInformation = ({
  prevStep,
  nextStep,
  selectedCountry,
  industryCategoryType,
  industryTemplate,
  order,
  showBackButton = true,
  hasKeapAccountAbility,
  partnerType,
}) => {
  const {state: locationState} = useLocation()
  const [emailRegistered, setEmailRegistered] = useState(true)
  const [keapAccount, setKeapAccount] = useState(
    order?.accountInformation?.keapAccount || false,
  )
  const [showPartnerDifferentModal, setShowPartnerDifferentModal] =
    useState(false)
  const [showBniRestrictedModal, setShowBniRestrictedModal] = useState(false)
  const [showSocialAdsBniRestrictedModal, setShowSocialAdsBniRestrictedModal] =
    useState(false)
  const [showMixSubscriptionModal, setShowMixSubscriptionModal] =
    useState(false)
  const [showLegacyContactsModal, setShowLegacyContactsModal] = useState(false)
  const [showMultipleParentModal, setShowMultipleParentModal] = useState(false)
  const [showAccountExistsModal, setShowAccountExistsModal] = useState(false)
  const [showAccountCreationFailedModal, setShowAccountCreationFailedModal] =
    useState(false)
  const [
    accountCreationFailedErrorMessage,
    setAccountCreationFailedErrorMessage,
  ] = useState(null)
  const [showUpgradeAccountModal, setShowUpgradeAccountModal] = useState(false)
  const [showMultiLocationModal, setShowMultiLocationModal] = useState(false)
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(false)
  const [listOfStates, setListOfStates] = useState([])
  const [existingThryvId, setExistingThryvId] = useState('')
  const [selectedThryvId, setSelectedThryvId] = useState()
  const [existingSubscriptionId, setExistingSubscriptionId] = useState('')
  const [validatedEmail, setValidatedEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isV5Upgrade, setIsV5Upgrade] = useState(false)
  const [thryvId, setThryvId] = useState('')
  const [keapID, setKeapID] = useState(order?.accountInformation?.keapID || '')
  const [isInvoiceBilled, setIsInvoiceBilled] = useState(false)
  const [showXpCode, setShowXpCode] = useState(false)
  const [showRepList, setShowRepList] = useState(false)
  const [locationData, setLocationData] = useState()
  const [filteredlocationData, setFilteredlocationData] = useState([])
  const [selectedRow, setSelectedRow] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const client = useAuthClient()
  const {roles} = useUserRoles()
  const {run} = useAsync({
    status: 'pending',
  })
  const navigate = useNavigate()
  const {user} = useAuth0()
  const {
    register,
    formState: {errors},
    control,
    handleSubmit,
    watch,
    trigger,
    setValue,
    reset,
    clearErrors,
  } = useForm({
    mode: 'onSubmit',
    defaultValues: {
      differentSalesRep: order.accountInformation?.behalf
        ? DIFFERENT_SALES_REP_OPTIONS[0]
        : order.accountInformation?.xpCode
        ? DIFFERENT_SALES_REP_OPTIONS[1]
        : roles.includes('PartnerOps')
        ? DIFFERENT_SALES_REP_OPTIONS[1]
        : DIFFERENT_SALES_REP_OPTIONS[2],
      overrideRep: OVERRIDE_SST_OPTIONS[1],
      email:
        locationState?.order.customerEmail ||
        order?.accountInformation?.email ||
        '',
      firstName:
        locationState?.order.customerFirstName ||
        order?.accountInformation?.firstName ||
        '',
      lastName:
        locationState?.order.customerLastName ||
        order?.accountInformation?.lastName ||
        '',
      xpCode:
        locationState?.order.xpCode || order?.accountInformation?.xpCode || '',
      company:
        locationState?.order.customerCompany ||
        order?.accountInformation?.company ||
        '',
      address:
        locationState?.order.customerAddress ||
        order?.accountInformation?.address ||
        '',
      city:
        locationState?.order.customerCity ||
        order?.accountInformation?.city ||
        '',
      postCode: formatPostalCode(
        locationState?.order.customerZip ||
          order?.accountInformation?.postCode ||
          '',
        selectedCountry,
      ),
      companyPhone: order?.accountInformation?.companyPhone || '',
      mobile: order?.accountInformation?.mobile || '',
      salesforceCustomerID:
        order?.accountInformation?.salesforceCustomerID || '',
      salesforceEmail: order?.accountInformation?.salesforceEmail || '',
      leadID: order?.accountInformation?.leadID || '',
      keapID: order?.accountInformation?.keapID || '',
    },
    reValidateMode: 'onChange',
    resolver: yupResolver(SCHEMA),
    context: {componentProps: {selectedCountry}},
  })
  setValue(
    'leadIDRequired',
    roles.includes('PremisePilot') || roles.includes('LeadConverter'),
  )
  setValue('xpCodeRequired', roles.includes('PartnerOps'))

  const isUS = selectedCountry === 'US'

  const [
    email,
    differentSalesRep,
    xpCode,
    salesRepCode,
    salesRepInfo,
    overrideRep,
    overrideRepCode,
    overrideRepInfo,
    state,
  ] = watch([
    'email',
    'differentSalesRep',
    'xpCode',
    'salesRepCode',
    'salesRepInfo',
    'overrideRep',
    'overrideRepCode',
    'overrideRepInfo',
    'state',
  ])

  const clearAccountInformationForm = React.useCallback(() => {
    reset({
      leadIDRequired:
        roles.includes('PremisePilot') || roles.includes('LeadConverter'),
      xpCodeRequired: roles.includes('PartnerOps'),
      email: '',
      firstName: '',
      lastName: '',
      xpCode: '',
      company: '',
      address: '',
      city: '',
      state: {name: '', value: ''},
      postCode: '',
      companyPhone: '',
      mobile: '',
      salesforceCustomerID: '',
      salesforceEmail: '',
      leadID: '',
      abn: '',
      keapID: '',
      keapAccount: false,
      differentSalesRep: roles.includes('PartnerOps')
        ? DIFFERENT_SALES_REP_OPTIONS[1]
        : DIFFERENT_SALES_REP_OPTIONS[2],
      salesRepCode: '',
      salesRepInfo: '',
      behalfFirst: '',
      behalfLast: '',
      behalfEmail: '',
      overrideRep: OVERRIDE_SST_OPTIONS[1],
      overrideRepCode: '',
      overrideRepInfo: '',
      overrideRepFirst: '',
      overrideRepLast: '',
      overrideRepEmail: '',
    })
    clearErrors()
    setEmailRegistered(true)
    setValidatedEmail('')
    setIsSubmitButtonDisabled(false)
    setKeapAccount(false)
    setKeapID('')
    setShowRepList(false)
    setShowXpCode(roles.includes('PartnerOps'))
    setShowAccountCreationFailedModal(false)
    setAccountCreationFailedErrorMessage(null)
  }, [clearErrors, reset, roles])

  const setData = formData => {
    console.log('formData in setData is ' + JSON.stringify(formData))
    let data = {
      ...formData,
      postCode: formData.postCode.replace(' ', ''),
      behalf: formData.salesRepCode,
      overrideRep: formData.overrideRepCode,
      xpCode: formData.xpCode,
      keapAccount: formData.keapAccount,
      keapID: formData.keapID,
      country: locationState?.order.selectedCountry.isoValue || selectedCountry,
      state: locationState?.order.customerState || formData.state.value,
      industryId: industryTemplate,
      software: industryCategoryType,
    }
    if ((salesRepCode || salesRepCode === '') && !differentSalesRep.value) {
      delete data.salesRepInfo
      delete data.salesRepCode
      delete data.behalf
      delete data.behalfEmail
      delete data.behalfFirst
      delete data.behalfLast
    }
    if ((overrideRepCode || overrideRepCode === '') && !overrideRep.value) {
      delete data.overrideRepInfo
      delete data.overrideRepCode
      delete data.overrideRep
      delete data.overrideRepEmail
      delete data.overrideRepFirst
      delete data.overrideRepLast
    }
    return data
  }

  const saveData = formData => {
    let data = {
      ...formData,
      industryCategoryType: industryCategoryType,
      behalf: formData.salesRepCode,
      overrideRepId: formData.overrideRepCode,
      xpCode: formData.xpCode,
      country: locationState?.order.selectedCountry.isoValue || selectedCountry,
      state: locationState?.order.customerState || formData.state.value,
      industryId: industryTemplate,
      software: industryCategoryType,
      keapAccount: formData.keapAccount,
      keapID: formData.keapID,
    }
    if ((salesRepCode || salesRepCode === '') && !differentSalesRep.value) {
      delete data.salesRepInfo
      delete data.salesRepCode
      delete data.behalf
      delete data.behalfEmail
      delete data.behalfFirst
      delete data.behalfLast
    }
    if ((overrideRepCode || overrideRepCode === '') && !overrideRep.value) {
      delete data.overrideRepInfo
      delete data.overrideRepCode
      delete data.overrideRep
      delete data.overrideRepEmail
      delete data.overrideRepFirst
      delete data.overrideRepLast
    }
    if (differentSalesRep.value !== false) {
      delete data.xpCode
    }
    if (partnerType && typeof partnerType?.value === 'boolean') {
      data.salesRepChannel = partnerType?.value
        ? 'Keap_Partner'
        : 'Thryv_Partner'
    }

    return data
  }

  const submitForm = async formData => {
    const data = saveData(formData)
    try {
      setIsSaving(true)
      await run(client('orders/', {data}))
      setIsSaving(false)
      toast.success('Form submitted successfully')
      setTimeout(() => {
        navigate('/orders')
      }, 5000)
    } catch (responseError) {
      setIsSaving(false)
      if (responseError.status !== 400) {
        throw responseError
      }
      responseError.errors.forEach(({message}) => toast.error(message))
    }
  }

  const handleNextStepData = async formData => {
    try {
      const accountInformationData = setData(formData)
      accountInformationData.keapAccount =
        accountInformationData.keapAccount || keapAccount
      accountInformationData.keapID = accountInformationData.keapID || keapID
      let nextStepData = {accountInformation: accountInformationData}

      const data = {
        email: accountInformationData.email,
        salesforceEmail: accountInformationData.salesforceEmail,
        firstName: accountInformationData.firstName,
        lastName: accountInformationData.lastName,
        city: accountInformationData.city,
        state: accountInformationData.state,
        country: accountInformationData.country,
        phone: accountInformationData.companyPhone,
        industryId: accountInformationData.industryId,
        mobile: accountInformationData.mobile,
        address: accountInformationData.address,
        company: accountInformationData.company,
        zipCode: accountInformationData.postCode,
        onBehalf: accountInformationData.behalf,
        xpCode: accountInformationData.xpCode,
        keapAccount: accountInformationData.keapAccount,
        keapID: accountInformationData.keapID,
      }

      if (
        formData.differentSalesRep.value === true ||
        formData.differentSalesRep.value === 'true'
      ) {
        delete data.xpCode
      } else if (
        formData.differentSalesRep.value === false ||
        formData.differentSalesRep.value === 'false' ||
        formData.differentSalesRep.value === 'No' ||
        formData.differentSalesRep.value === 'NO'
      ) {
        delete data.onBehalf
        delete data.xpCode
      }

      if (partnerType && typeof partnerType?.value === 'boolean') {
        data.salesRepChannel = partnerType?.value
          ? 'Keap_Partner'
          : 'Thryv_Partner'
      }

      if (!data.salesforceEmail) delete data.salesforceEmail
      if (!data.onBehalf) delete data.onBehalf
      if (!data.xpCode) delete data.xpCode
      if (!data.keapID) delete data.keapID
      setIsLoading(true)
      const response = await run(client('accounts/5', {data, method: 'POST'}))
      setIsLoading(false)
      nextStepData = {...nextStepData, CCAccountInfo: response}
      nextStep(nextStepData)
    } catch (err) {
      setIsLoading(false)
      if (err?.message) {
        toast.error(err.message)
      } else {
        setShowAccountCreationFailedModal(true)
      }
    }
  }

  React.useEffect(() => {
    const lower = searchTerm?.toLowerCase() ?? ''
    if (lower) {
      const filtered = locationData.filter(
        location =>
          location?.company?.toLowerCase().includes(lower) ||
          location?.address?.toLowerCase().includes(lower) ||
          location?.phone?.toLowerCase().includes(lower) ||
          location?.email?.toLowerCase().includes(lower) ||
          location?.code?.toLowerCase().includes(lower),
      )
      setFilteredlocationData(filtered)
    } else {
      setFilteredlocationData(locationData)
    }
  }, [searchTerm, locationData])

  React.useEffect(async () => {
    const data = fetchSelectedCountryRegionList(selectedCountry)
    setListOfStates(data)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    const shouldDisableSubmitButton = (repInfo, rep) => {
      if (
        rep.value === true &&
        (repInfo === SALES_REP_INFO_NOT_FOUND || !repInfo)
      ) {
        return true
      }
      return false
    }
    if (
      shouldDisableSubmitButton(salesRepInfo, differentSalesRep) ||
      shouldDisableSubmitButton(overrideRepInfo, overrideRep)
    ) {
      setIsSubmitButtonDisabled(true)
    } else {
      setIsSubmitButtonDisabled(false)
    }
  }, [salesRepInfo, differentSalesRep, overrideRepInfo, overrideRep])

  React.useEffect(() => {
    if (order.savedOrder) {
      setValue('email', order.accountInformation.email)
      setValue('mobile', order.accountInformation.mobile)
      setValue('firstName', order.accountInformation.firstName)
      setValue('lastName', order.accountInformation.lastName)
      setValue('company', order.accountInformation.company)
      setValue('address', order.accountInformation.address)
      setValue('city', order.accountInformation.city)
      setValue('postCode', order.accountInformation.postCode)
      setValue('companyPhone', order.accountInformation.companyPhone)
      setValue(
        'state',
        listOfStates.find(x => x.value === order.accountInformation.state),
      )
    }
    if (order.fromDemo) {
      setValue('firstName', order.customerFirstName)
      setValue('lastName', order.customerLastName)
      setValue('company', order.customerCompany)
      setValue('email', order.customerEmail)
      setValue('mobile', order.customerPhone)
      setValue('address', order.customerAddress)
      setValue('city', order.customerCity)
      setValue('postCode', order.customerZip)
      setValue(
        'state',
        listOfStates.find(x => x.value === order.customerState),
      )
    }
  }, [order, setValue, listOfStates, user, differentSalesRep])

  React.useEffect(() => {
    if (differentSalesRep.value) {
      setShowRepList(true)
      setShowXpCode(false)
    }
    if (!differentSalesRep.value) {
      setShowRepList(false)
      setShowXpCode(true)
    }
    if (differentSalesRep.value === 'No') {
      setShowXpCode(false)
      setShowRepList(false)
    }
    if (!differentSalesRep.value && xpCode !== null && xpCode !== '') {
      setShowXpCode(true)
    }
  }, [differentSalesRep, xpCode])

  React.useEffect(() => {
    if (!order?.accountInformation?.state) return
    setValue(
      'state',
      listOfStates.find(x => x.value === order.accountInformation.state),
    )
  }, [listOfStates, order?.accountInformation?.state, setValue])

  React.useEffect(() => {
    if (!order?.accountInformation?.salesRepName) return
    // setValue('salesRepCode', order.accountInformation.salesRepCode)
    setValue('salesRepInfo', order.accountInformation.salesRepName)
    // setValue('differentSalesRep', order.accountInformation.differentSalesRep)
  }, [
    order?.accountInformation?.differentSalesRep,
    order?.accountInformation?.salesRepCode,
    order?.accountInformation?.salesRepName,
    setValue,
  ])

  React.useEffect(() => {
    if (!order?.accountInformation?.overrideRepCode) return
    setValue('overrideRepCode', order.accountInformation.overrideRepCode)
    setValue('overrideRepInfo', order.accountInformation.overrideRepInfo)
    setValue('overrideRep', order.accountInformation.overrideRep)
  }, [
    order?.accountInformation?.overrideRep,
    order?.accountInformation?.overrideRepCode,
    order?.accountInformation?.overrideRepInfo,
    setValue,
  ])

  return (
    <>
      {isLoading && <LoadingTimelineModal />}
      {isSaving && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <Spinner />
        </div>
      )}
      {showAccountExistsModal && (
        <Modal
          altBtn={<></>}
          openOnLoad
          variant="default"
          footer
          action
          btnAction={() => setShowAccountExistsModal(false)}
          onClose={() => setShowAccountExistsModal(false)}
          btnActionText="Ok"
          hideX
        >
          An account with this email already exists. <br />
          Please enter a different email address.
        </Modal>
      )}
      {showPartnerDifferentModal && (
        <Modal
          altBtn={<></>}
          openOnLoad
          variant="default"
          footer
          action
          btnAction={() => setShowPartnerDifferentModal(false)}
          onClose={() => setShowPartnerDifferentModal(false)}
          btnActionText="Ok"
          hideX
        >
          Oops! Looks like this account was sold by another channel. Please
          reach out to the partner management team for assistance. Thank you!
        </Modal>
      )}
      {showBniRestrictedModal && (
        <Modal
          altBtn={<></>}
          openOnLoad
          variant="default"
          footer
          action
          btnAction={() => setShowBniRestrictedModal(false)}
          onClose={() => setShowBniRestrictedModal(false)}
          btnActionText="Ok"
          hideX
        >
          You have no access to accounts with the product Leads Lift BNI.
        </Modal>
      )}
      {showSocialAdsBniRestrictedModal && (
        <Modal
          altBtn={<></>}
          openOnLoad
          variant="default"
          footer
          action
          btnAction={() => setShowSocialAdsBniRestrictedModal(false)}
          onClose={() => setShowSocialAdsBniRestrictedModal(false)}
          btnActionText="Ok"
          hideX
        >
          You have no access to accounts with the product Social Ads BNI.
        </Modal>
      )}
      {showMixSubscriptionModal && (
        <Modal
          altBtn={<></>}
          openOnLoad
          variant="lg"
          footer
          action
          btnAction={() => setShowMixSubscriptionModal(false)}
          onClose={() => setShowMixSubscriptionModal(false)}
          btnActionText="Ok"
          hideX
        >
          ✅ Quick fix needed: This account has both 4.0 and 5.0 plans, which is
          holding up the order. We’re here to help—reach Client Care at
          866-519-0475, Option 2, and we’ll sort it out together.
        </Modal>
      )}
      {showLegacyContactsModal && (
        <Modal
          altBtn={<></>}
          openOnLoad
          variant="lg"
          footer
          action
          btnAction={() => setShowLegacyContactsModal(false)}
          onClose={() => setShowLegacyContactsModal(false)}
          btnActionText="Ok"
          hideX
        >
          <div style={{padding: '20px', textAlign: 'center'}}>
            <strong>
              A little blast from the past! This account contains Legacy
              Contacts.
            </strong>
            <br></br>
            For proper handling, please reach out to Client Care at{' '}
            <strong>844-339-7494(Option 1).</strong>
          </div>
        </Modal>
      )}
      {showMultipleParentModal && (
        <Modal
          altBtn={<></>}
          openOnLoad
          variant="lg"
          footer
          action
          btnAction={() => setShowMultipleParentModal(false)}
          onClose={() => setShowMultipleParentModal(false)}
          btnActionText="Ok"
          hideX
        >
          Oops! Looks like this account is not setup correctly. Please reach out
          to client care. Thank you!
        </Modal>
      )}
      {showUpgradeAccountModal && (
        <Modal
          altBtn={<></>}
          openOnLoad
          variant="lg"
          footer
          action
          btnAction={() => {
            let {
              selectedCountry,
              industryCategoryId,
              industryCategoryType,
              industryCategoryDescription,
              selectedIndustryCategory,
              template,
            } = order
            if (roles.includes('PartnerOps')) {
              order = {
                selectedCountry,
                industryCategoryId,
                industryCategoryType,
                industryCategoryDescription,
                selectedIndustryCategory,
                template,
              }
            }
            navigate('/order/upgrade', {
              state: {
                order: {
                  billingAccountId: existingSubscriptionId,
                  subscriptionId: existingSubscriptionId,
                  thryvId: existingThryvId,
                  v5upgrade: isV5Upgrade,
                  ...order,
                  ...(isV5Upgrade && {
                    CCAccountInfo: {thryvId: existingThryvId},
                  }),
                },
                currentStep: 1,
              },
            })
          }}
          onClose={() => setShowUpgradeAccountModal(false)}
          btnActionText="Update"
          hideX
          footerCancel
        >
          <div className="text-center">
            <ModalTitle variant="primary">
              An account with this email already exists
            </ModalTitle>
            <ParagraphText variant="reg" className="mt-1">
              Do you want to update this Thryv?
            </ParagraphText>
          </div>
        </Modal>
      )}
      {showAccountCreationFailedModal && (
        <AccountFailedModalComponent
          errorMessage={accountCreationFailedErrorMessage}
          onOk={
            accountCreationFailedErrorMessage
              ? () => {
                  setShowAccountCreationFailedModal(false)
                  setAccountCreationFailedErrorMessage(null)
                }
              : clearAccountInformationForm
          }
        />
      )}
      {showMultiLocationModal && (
        <Modal
          altBtn={<></>}
          openOnLoad
          variant="lg"
          footer
          action
          btnAction={() => {
            let {
              selectedCountry,
              industryCategoryId,
              industryCategoryType,
              industryCategoryDescription,
              selectedIndustryCategory,
              template,
            } = order
            if (roles.includes('PartnerOps')) {
              order = {
                selectedCountry,
                industryCategoryId,
                industryCategoryType,
                industryCategoryDescription,
                selectedIndustryCategory,
                template,
              }
            }
            navigate('/order/upgrade', {
              state: {
                order: {
                  billingAccountId: existingSubscriptionId,
                  subscriptionId: existingSubscriptionId,
                  thryvId: selectedThryvId,
                  v5upgrade: isV5Upgrade,
                  ...order,
                  ...(isV5Upgrade && {
                    CCAccountInfo: {thryvId: selectedThryvId},
                  }),
                },
                currentStep: 1,
              },
            })
          }}
          btnActionText="Update"
          btnActionDisabled={selectedThryvId ? false : true}
          onClose={() => {
            setShowMultiLocationModal(false)
            setSelectedThryvId(null)
          }}
          // handleCancelAction={() => {
          //   setSelectedThryvId(null)
          // }}
          onOpen={async () => {
            setSelectedRow(null)
            if (existingThryvId) {
              const response = await run(
                client('accounts/getAccountHierarchy/' + existingThryvId),
              )
              setLocationData(response)
              if (response.length === 1) {
                setSelectedRow(response[0])
                setSelectedThryvId(response[0].code)
              }
            }
          }}
          disableBackdropClick
          hideX
          footerCancel
        >
          <div>
            <h2>
              <b>Locations</b>
            </h2>
            <h4>Select one of the below child locations to update</h4>
            <input
              type="text"
              placeholder="Search by Company, Address, Phone, Email, Thryv Id..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{
                marginBottom: '15px',
                padding: '8px',
                width: '50%',
              }}
            />
            <table
              border="1"
              cellPadding="10"
              style={{borderCollapse: 'collapse'}}
            >
              <thead>
                <tr>
                  <th>Select</th>
                  <th>Company</th>
                  <th>Address</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Thryv ID</th>
                </tr>
              </thead>
              <tbody>
                {filteredlocationData?.map(location => (
                  <tr
                    key={location.id}
                    onClick={() => {
                      setSelectedRow(location)
                      setSelectedThryvId(location.code)
                    }}
                    style={{
                      backgroundColor:
                        selectedRow?.id === location.id ? '#d3f9d8' : 'white',
                      cursor: 'pointer',
                    }}
                  >
                    <td>
                      <input
                        type="radio"
                        checked={selectedRow?.id === location.id}
                        onChange={() => {
                          setSelectedRow(location)
                          setSelectedThryvId(location.code)
                        }}
                      />
                    </td>
                    <td>{location.company}</td>
                    <td>{location.address}</td>
                    <td>{location.phone}</td>
                    <td>{location.email}</td>
                    <td>{location.code}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Selected Row */}
            {selectedRow && (
              <div style={{marginTop: '20px'}}>
                <h3>
                  <b>Selected Location :</b>
                </h3>
                <p>
                  <strong>Company:</strong> {selectedRow.company}
                </p>
                <p>
                  <strong>Address:</strong> {selectedRow.address}
                </p>
                <p>
                  <strong>Phone:</strong> {selectedRow.phone}
                </p>
                <p>
                  <strong>Email:</strong> {selectedRow.email}
                </p>
                <p>
                  <strong>Thryv ID:</strong> {selectedRow.code}
                </p>
              </div>
            )}
          </div>
        </Modal>
      )}
      <NewOrderLayout
        prevStep={prevStep}
        showContinueButton={false}
        title={!isInvoiceBilled ? 'Account Information' : 'Invoices'}
        style={(isLoading || isSaving) && {pointerEvents: 'none'}}
        showBackButton={showBackButton}
      >
        {isInvoiceBilled ? (
          <InvoiceInformation
            thryvId={thryvId}
            email={email}
            setIsInvoiceBilled={setIsInvoiceBilled}
            selectedCountry={selectedCountry}
            nextStep={nextStep}
          />
        ) : (
          <AccountInformationForm
            register={register}
            errors={errors}
            listOfStates={listOfStates}
            selectedCountry={selectedCountry}
            salesRepInfo={salesRepInfo}
            overrideRepInfo={overrideRepInfo}
            differentSalesRep={differentSalesRep}
            overrideRep={overrideRep}
            validatedEmail={validatedEmail}
            email={email}
            xpCode={xpCode}
            emailRegistered={emailRegistered}
            setEmailRegistered={setEmailRegistered}
            setValidatedEmail={setValidatedEmail}
            setExistingThryvId={setExistingThryvId}
            setExistingSubscriptionId={setExistingSubscriptionId}
            setIsV5Upgrade={setIsV5Upgrade}
            setShowPartnerDifferentModal={setShowPartnerDifferentModal}
            setShowBniRestrictedModal={setShowBniRestrictedModal}
            setShowSocialAdsBniRestrictedModal={
              setShowSocialAdsBniRestrictedModal
            }
            setShowMixSubscriptionModal={setShowMixSubscriptionModal}
            setShowLegacyContactsModal={setShowLegacyContactsModal}
            setShowMultipleParentModal={setShowMultipleParentModal}
            setShowUpgradeAccountModal={setShowUpgradeAccountModal}
            setShowAccountExistsModal={setShowAccountExistsModal}
            setShowMultiLocationModal={setShowMultiLocationModal}
            setIsInvoiceBilled={setIsInvoiceBilled}
            setValue={setValue}
            salesRepCode={salesRepCode}
            overrideRepCode={overrideRepCode}
            trigger={trigger}
            isUS={isUS}
            roles={roles}
            control={control}
            state={state}
            order={order}
            user={user}
            setThryvId={setThryvId}
            isSubmitButtonDisabled={isSubmitButtonDisabled}
            handleSubmit={handleSubmit}
            submitForm={submitForm}
            handleNextStepData={handleNextStepData}
            showXpCode={showXpCode}
            setShowXpCode={setShowXpCode}
            showRepList={showRepList}
            setShowRepList={setShowRepList}
            hasKeapAccountAbility={hasKeapAccountAbility}
            keapAccount={keapAccount}
            setKeapAccount={setKeapAccount}
            keapID={keapID}
            setKeapID={setKeapID}
            isSaving={isSaving}
          />
        )}
      </NewOrderLayout>
    </>
  )
}

export {AccountInformation}
